import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    Activity,
    AlertCircle,
    Bell,
    ChevronDown,
    Code2,
    Command,
    Eye,
    FilePlus,
    Globe,
    HelpCircle,
    Keyboard,
    LogOut,
    Menu,
    PlusCircle,
    Search,
    Settings,
    TrendingUp,
    User,
    X
} from 'lucide-react';
import {toggleSidebar} from '@/store/features/uiSlice';
import {logout, selectIsAuthenticated, selectUser} from '@/store/features/authSlice';
import {useNavigate} from 'react-router-dom';

const Header = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector(selectUser);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const { sidebarOpen, sidebarMinimized } = useSelector((state) => state.ui);

    const [searchQuery, setSearchQuery] = useState('');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showQuickAddMenu, setShowQuickAddMenu] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [unreadNotifications, setUnreadNotifications] = useState(5);

    const notificationsRef = useRef(null);
    const userMenuRef = useRef(null);
    const quickAddMenuRef = useRef(null);
    const searchInputRef = useRef(null);

    // Notifications avec données de visites
    const [notifications, setNotifications] = useState([
        {
            id: 1,
            text: 'Nouvelle visite sur Portfolio',
            time: '2 min',
            read: false,
            type: 'visite',
            icon: <Eye size={14} />,
            site: 'portfolio.com',
            visitor: 'France'
        },
        {
            id: 2,
            text: 'Site Blog Tech a atteint 1000 visites',
            time: '1h',
            read: false,
            type: 'milestone',
            icon: <TrendingUp size={14} />,
            site: 'blog-tech.com',
            milestone: '1000'
        },
        {
            id: 3,
            text: 'Site hors ligne détecté',
            time: '3h',
            read: true,
            type: 'alert',
            icon: <AlertCircle size={14} />,
            site: 'shop.com',
            alert: 'offline'
        },
        {
            id: 4,
            text: 'Pic de visites inhabituel',
            time: '5h',
            read: false,
            type: 'alert',
            icon: <Activity size={14} />,
            site: 'portfolio.com',
            alert: 'spike'
        },
        {
            id: 5,
            text: 'Nouveau record journalier',
            time: '1j',
            read: false,
            type: 'milestone',
            icon: <TrendingUp size={14} />,
            site: 'docs.com',
            milestone: '156'
        },
    ]);

    // Quick add options adaptées
    const quickAddOptions = [
        {
            id: 'site',
            label: 'Nouveau site',
            icon: <Globe size={16} />,
            description: 'Ajouter un site à monitorer'
        },
        {
            id: 'note',
            label: 'Nouvelle note',
            icon: <FilePlus size={16} />,
            description: 'Document, article, résumé'
        },
        {
            id: 'snippet',
            label: 'Nouveau snippet',
            icon: <Code2 size={16} />,
            description: 'Code réutilisable'
        },
        {
            id: 'command',
            label: 'Nouvelle commande',
            icon: <Command size={16} />,
            description: 'Commande terminal'
        },
    ];

    // Gestion des clics en dehors des menus
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
            if (quickAddMenuRef.current && !quickAddMenuRef.current.contains(event.target)) {
                setShowQuickAddMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Raccourci clavier Ctrl+K pour la recherche
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
            if (e.key === 'Escape') {
                setCommandPaletteOpen(false);
                setShowNotifications(false);
                setShowUserMenu(false);
                setShowQuickAddMenu(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleLogout = useCallback(() => {
        dispatch(logout());
        navigate('/login');
        setShowUserMenu(false);
    }, [dispatch, navigate]);

    const handleNotificationClick = (id) => {
        // Marquer comme lu
        const notification = notifications.find(n => n.id === id);
        if (notification && !notification.read) {
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, read: true } : n
            ));
            setUnreadNotifications(prev => Math.max(0, prev - 1));

            // Navigation selon le type
            if (notification.type === 'visite' || notification.type === 'milestone') {
                navigate('/sites');
            } else if (notification.type === 'alert') {
                navigate('/visites');
            }
        }
    };

    const handleQuickAdd = (type) => {
        switch(type) {
            case 'site':
                navigate('/sites/new');
                break;
            case 'note':
                navigate('/notes/new');
                break;
            case 'snippet':
                navigate('/snippets/new');
                break;
            case 'command':
                navigate('/commands/new');
                break;
            default:
                break;
        }
        setShowQuickAddMenu(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
        }
    };

    const getUserInitials = () => {
        if (!user?.fullname) return 'SM';
        return user.fullname
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const userMenuItems = [
        { id: 'profile', label: 'Mon profil', icon: <User size={16} />, action: () => navigate('/profile') },
        { id: 'sites', label: 'Mes sites', icon: <Globe size={16} />, action: () => navigate('/sites') },
        { id: 'visites', label: 'Visites', icon: <Eye size={16} />, action: () => navigate('/visites') },
        { id: 'divider1', type: 'divider' },
        { id: 'settings', label: 'Paramètres', icon: <Settings size={16} />, action: () => navigate('/settings') },
        { id: 'divider2', type: 'divider' },
        { id: 'logout', label: 'Déconnexion', icon: <LogOut size={16} />, action: handleLogout },
    ];

    // Marquer toutes les notifications comme lues
    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadNotifications(0);
    };

    // Statut système rapide
    const systemStatus = {
        sitesOnline: 3,
        totalSites: 5,
        visitsToday: 42,
        activeVisitors: 7
    };

    return (
        <>
            <header className="
                bg-white border-b border-gray-200
                px-4 py-3 shadow-sm sticky top-0 z-40
            ">
                <div className="flex items-center justify-between">
                    {/* Left section */}
                    <div className="flex items-center gap-3">
                        {/* Menu toggle */}
                        <button
                            onClick={() => dispatch(toggleSidebar())}
                            className="
                                p-2 rounded-lg hover:bg-gray-100
                                transition-colors text-gray-700
                                md:hidden
                            "
                            aria-label={sidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
                        >
                            <Menu size={20} />
                        </button>

                        {/* Logo/Brand */}
                        <div
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={() => navigate('/')}
                        >
                            <div className="p-1.5 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg">
                                <Globe size={16} className="text-white" />
                            </div>
                            <div className="hidden md:block">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-800">
                                        SiteMonitor
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                        <span className="text-xs text-gray-500">
                                            {systemStatus.activeVisitors} actifs
                                        </span>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                    <span>{systemStatus.sitesOnline}/{systemStatus.totalSites} sites en ligne</span>
                                    <span>•</span>
                                    <span>{systemStatus.visitsToday} visites aujourd'hui</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick add button with dropdown */}
                        <div className="relative hidden md:block" ref={quickAddMenuRef}>
                            <button
                                onClick={() => setShowQuickAddMenu(!showQuickAddMenu)}
                                className="
                                    flex items-center gap-2 px-3 py-1.5
                                    bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800
                                    text-white rounded-lg text-sm font-medium transition-all
                                    shadow-md hover:shadow-lg
                                "
                            >
                                <PlusCircle size={16} />
                                <span>Ajouter</span>
                                <ChevronDown size={14} className={showQuickAddMenu ? 'rotate-180' : ''} />
                            </button>

                            {/* Quick add dropdown */}
                            {showQuickAddMenu && (
                                <div className="
                                    absolute top-full left-0 mt-2 w-64 bg-white
                                    rounded-lg shadow-xl border border-gray-200
                                    py-2 z-50
                                ">
                                    <div className="px-4 py-2 border-b border-gray-100">
                                        <p className="text-sm font-medium text-gray-800">
                                            Ajouter rapidement
                                        </p>
                                    </div>
                                    {quickAddOptions.map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() => handleQuickAdd(option.id)}
                                            className="
                                                w-full px-4 py-3 flex items-start gap-3
                                                hover:bg-gray-50
                                                transition-colors text-left
                                            "
                                        >
                                            <div className="p-1.5 bg-blue-50 rounded-lg">
                                                {React.cloneElement(option.icon, {
                                                    size: 16,
                                                    className: "text-blue-600"
                                                })}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-800">
                                                    {option.label}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {option.description}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Center - Search */}
                    <div className="flex-1 max-w-2xl mx-4">
                        <form onSubmit={handleSearch} className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={18} className="text-gray-400" />
                            </div>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher sites, visites, notes..."
                                className="
                                    w-full pl-10 pr-24 py-2.5 bg-gray-50
                                    border border-gray-300
                                    rounded-lg text-sm focus:outline-none focus:ring-2
                                    focus:ring-blue-500 focus:border-transparent
                                    placeholder-gray-500
                                    text-gray-900
                                    transition-colors
                                "
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
                                <kbd className="
                                    px-2 py-1 text-xs font-mono bg-gray-200
                                    text-gray-600 rounded border border-gray-300
                                    hidden sm:inline-flex
                                ">
                                    Ctrl+K
                                </kbd>
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="p-1 hover:bg-gray-200 rounded"
                                    >
                                        <X size={14} className="text-gray-500" />
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Right section */}
                    <div className="flex items-center gap-2">
                        {/* Command palette trigger */}
                        <button
                            onClick={() => setCommandPaletteOpen(true)}
                            className="
                                hidden lg:flex items-center gap-2 px-3 py-1.5
                                bg-gray-100 hover:bg-gray-200
                                rounded-lg text-sm text-gray-700
                                transition-colors
                            "
                            title="Command palette (Ctrl+Shift+P)"
                        >
                            <Keyboard size={14} />
                            <span>⌘</span>
                        </button>

                        {/* Stats rapides */}
                        <div className="hidden md:flex items-center gap-4 px-3 border-r border-gray-300">
                            <div className="text-right">
                                <div className="text-sm font-medium text-gray-800">
                                    {systemStatus.visitsToday}
                                </div>
                                <div className="text-xs text-gray-500">
                                    visites
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm font-medium text-gray-800 flex items-center gap-1">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    {systemStatus.sitesOnline}/{systemStatus.totalSites}
                                </div>
                                <div className="text-xs text-gray-500">
                                    sites
                                </div>
                            </div>
                        </div>

                        {/* Help */}
                        <button
                            onClick={() => navigate('/help')}
                            className="
                                p-2 rounded-lg hover:bg-gray-100
                                transition-colors text-gray-700
                            "
                            title="Aide et documentation"
                        >
                            <HelpCircle size={18} />
                        </button>

                        {/* Notifications */}
                        <div className="relative" ref={notificationsRef}>
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="
                                    p-2 rounded-lg hover:bg-gray-100
                                    transition-colors text-gray-700 relative
                                "
                                title="Notifications"
                            >
                                <Bell size={18} />
                                {unreadNotifications > 0 && (
                                    <span className="
                                        absolute -top-1 -right-1 w-5 h-5 bg-red-500
                                        rounded-full border-2 border-white
                                        text-xs text-white flex items-center justify-center
                                        animate-pulse
                                    ">
                                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                                    </span>
                                )}
                            </button>

                            {/* Notifications dropdown */}
                            {showNotifications && (
                                <div className="
                                    absolute top-full right-0 mt-2 w-96 bg-white
                                    rounded-lg shadow-xl border border-gray-200
                                    z-50 max-h-96 overflow-hidden
                                ">
                                    <div className="p-4 border-b border-gray-100">
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium text-gray-800">
                                                Notifications
                                            </p>
                                            <div className="flex items-center gap-2">
                                                {unreadNotifications > 0 && (
                                                    <button
                                                        onClick={markAllAsRead}
                                                        className="text-xs text-blue-600 hover:text-blue-700"
                                                    >
                                                        Tout marquer comme lu
                                                    </button>
                                                )}
                                                <span className="
                                                    px-2 py-1 text-xs font-medium bg-blue-100
                                                    text-blue-800 rounded-full
                                                ">
                                                    {unreadNotifications} non lue{unreadNotifications > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="overflow-y-auto max-h-64">
                                        {notifications.length > 0 ? (
                                            <div className="divide-y divide-gray-100">
                                                {notifications.map((notification) => (
                                                    <div
                                                        key={notification.id}
                                                        onClick={() => handleNotificationClick(notification.id)}
                                                        className={`
                                                            px-4 py-3 flex items-start gap-3 cursor-pointer
                                                            hover:bg-gray-50
                                                            transition-colors
                                                            ${!notification.read ? 'bg-blue-50/50' : ''}
                                                            ${notification.type === 'alert' ? 'bg-red-50/50' : ''}
                                                        `}
                                                    >
                                                        <div className={`
                                                            p-2 rounded-lg mt-0.5 flex-shrink-0
                                                            ${notification.type === 'visite' ? 'bg-blue-100 text-blue-600' :
                                                            notification.type === 'milestone' ? 'bg-green-100 text-green-600' :
                                                                notification.type === 'alert' ? 'bg-red-100 text-red-600' :
                                                                    'bg-gray-100 text-gray-600'}
                                                        `}>
                                                            {notification.icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-800">
                                                                {notification.text}
                                                            </p>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                {notification.site && (
                                                                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                                        {notification.site}
                                                                    </span>
                                                                )}
                                                                {notification.visitor && (
                                                                    <span className="text-xs text-gray-500">
                                                                        {notification.visitor}
                                                                    </span>
                                                                )}
                                                                {notification.milestone && (
                                                                    <span className="text-xs font-medium text-green-600">
                                                                        {notification.milestone}
                                                                    </span>
                                                                )}
                                                                <span className="text-xs text-gray-500">
                                                                    Il y a {notification.time}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {!notification.read && (
                                                            <div className={`
                                                                w-2 h-2 rounded-full mt-2 flex-shrink-0
                                                                ${notification.type === 'alert' ? 'bg-red-500' : 'bg-blue-500'}
                                                            `}></div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="p-8 text-center">
                                                <Bell size={32} className="mx-auto text-gray-400 mb-2" />
                                                <p className="text-gray-500">
                                                    Aucune notification
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-3 border-t border-gray-100">
                                        <button
                                            onClick={() => {
                                                navigate('/notifications');
                                                setShowNotifications(false);
                                            }}
                                            className="
                                                w-full text-sm text-center text-blue-600
                                                hover:text-blue-700 font-medium
                                            "
                                        >
                                            Voir toutes les notifications
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* User profile */}
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="
                                    flex items-center gap-3 pl-3
                                    hover:opacity-90 transition-opacity
                                "
                                disabled={!isAuthenticated}
                            >
                                <div className="relative">
                                    <div className="
                                        w-9 h-9 rounded-full bg-gradient-to-br
                                        from-blue-500 to-blue-600 flex items-center
                                        justify-center text-white font-bold text-sm
                                    ">
                                        {getUserInitials()}
                                    </div>
                                    {isAuthenticated && (
                                        <div className="
                                            absolute -bottom-0.5 -right-0.5 w-3 h-3
                                            bg-green-500 rounded-full border-2
                                            border-white
                                        "></div>
                                    )}
                                </div>
                                <div className="hidden md:block text-left">
                                    <p className="text-sm font-medium text-gray-800">
                                        {user?.fullname || 'Invité'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {user?.roles?.includes('ROLE_ADMIN') ? 'Administrateur' :
                                            user ? 'Gestionnaire Sites' : 'Non connecté'}
                                    </p>
                                </div>
                                <ChevronDown
                                    size={16}
                                    className={`text-gray-500 transition-transform ${
                                        showUserMenu ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>

                            {/* User menu dropdown */}
                            {showUserMenu && (
                                <div className="
                                    absolute top-full right-0 mt-2 w-56 bg-white
                                    rounded-lg shadow-xl border border-gray-200
                                    py-2 z-50
                                ">
                                    {isAuthenticated ? (
                                        <>
                                            <div className="px-4 py-3 border-b border-gray-100">
                                                <p className="text-sm font-medium text-gray-800">
                                                    {user?.fullname}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">
                                                    {user?.email}
                                                </p>
                                            </div>

                                            <div className="py-2">
                                                {userMenuItems.map((item) => {
                                                    if (item.type === 'divider') {
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className="my-2 border-t border-gray-100"
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <button
                                                            key={item.id}
                                                            onClick={item.action}
                                                            className="
                                                                w-full px-4 py-2.5 flex items-center gap-3
                                                                hover:bg-gray-50
                                                                transition-colors text-left
                                                                text-sm text-gray-700
                                                            "
                                                        >
                                                            {item.icon}
                                                            <span>{item.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-4">
                                            <p className="text-sm text-gray-600 mb-3">
                                                Connectez-vous pour gérer vos sites
                                            </p>
                                            <button
                                                onClick={() => {
                                                    navigate('/login');
                                                    setShowUserMenu(false);
                                                }}
                                                className="
                                                    w-full px-4 py-2 bg-blue-600 text-white
                                                    rounded-lg text-sm font-medium
                                                    hover:bg-blue-700 transition-colors
                                                "
                                            >
                                                Se connecter
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Command Palette Modal */}
            {commandPaletteOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20"
                    onClick={() => setCommandPaletteOpen(false)}
                >
                    <div
                        className="
                            w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl
                            border border-gray-200
                        "
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <Search size={20} className="text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Tapez une commande ou recherchez..."
                                    className="
                                        flex-1 bg-transparent border-none outline-none
                                        text-gray-900 placeholder-gray-500
                                    "
                                    autoFocus
                                />
                                <button
                                    onClick={() => setCommandPaletteOpen(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            <p className="text-sm text-gray-500 mb-3">
                                Commandes rapides
                            </p>
                            <div className="space-y-2">
                                {quickAddOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            handleQuickAdd(option.id);
                                            setCommandPaletteOpen(false);
                                        }}
                                        className="
                                            w-full px-4 py-3 flex items-center gap-3
                                            hover:bg-gray-50 rounded-lg text-left
                                        "
                                    >
                                        <div className="p-1.5 bg-gray-100 rounded">
                                            {option.icon}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">
                                                {option.label}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {option.description}
                                            </p>
                                        </div>
                                        <span className="ml-auto text-xs text-gray-400">
                                            ⌘ + {option.id.charAt(0).toUpperCase()}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;